import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner } from '@fortawesome/free-solid-svg-icons/faSpinner'
import { AppTypes } from '@src/reducers/app.reducer'
import WithdrawConfirmPopup from './WithdrawView.popup'
import { store } from '../../store'
import { CurrencyList } from '@src/service/coins/coins.types'

export const WithdrawButton = ({
  isLoadingSubmission,
  amount,
  address,
  isExternalCurrency,
  network,
  selectedUnit,
  activeWallet,
  memo,
  show = true,
  setPopup,
  clearForm,
  setIsLoadingSubmission,
  fetchPastWithdraws,
}: {
  isLoadingSubmission: boolean
  amount?: string
  address?: string
  isExternalCurrency: CurrencyList
  network?: string
  selectedUnit: string
  show?: boolean
  activeWallet?: { currency: string; virtualId: number }
  memo?: string
  setPopup: (popup: React.ReactNode) => void
  clearForm: () => void
  setIsLoadingSubmission: (loading: boolean) => void
  fetchPastWithdraws: () => void
}) => {
  if (!show) {
    return null
  }

  const { t } = useTranslation()
  const showNotification = useCallback(
    (status: string, text: string) =>
      store.dispatch({ type: AppTypes.SHOW_NOTIFICATION, notification: { status, text } }),
    []
  )

  const handleWithdrawClick = useCallback(() => {
    const invalidExternal = isExternalCurrency && !network
    const hasRequiredFields = amount && address && !invalidExternal

    if (hasRequiredFields) {
      setPopup(
        <WithdrawConfirmPopup
          unit={selectedUnit}
          amount={amount}
          currency={activeWallet?.currency}
          virtualId={activeWallet?.virtualId}
          address={address}
          network={network}
          memo={memo}
          setPopup={setPopup}
          clearForm={clearForm}
          setIsLoadingSubmission={setIsLoadingSubmission}
          onSubmit={fetchPastWithdraws}
        />
      )
    } else {
      showNotification('error', t('wallet.withdrawView.withDrawError', 'Correct amount and address should be provided'))
    }
  }, [
    amount,
    address,
    isExternalCurrency,
    network,
    selectedUnit,
    activeWallet,
    memo,
    setPopup,
    clearForm,
    setIsLoadingSubmission,
    fetchPastWithdraws,
    showNotification,
    t,
  ])

  return (
    <button disabled={isLoadingSubmission} className="button" onClick={handleWithdrawClick}>
      {isLoadingSubmission ? (
        <div>
          <FontAwesomeIcon icon={faSpinner} spin className="text-base" />
        </div>
      ) : (
        <span className="uppercase">{t('wallet.withdrawView.withdraw', 'Withdraw')}</span>
      )}
    </button>
  )
}

export default WithdrawButton
